import { useStripe } from "@stripe/react-stripe-js";
import moment from "moment";
import React, { useState } from "react";
import PlaidLink from "react-plaid-link";
import { connect, ConnectedProps } from "react-redux";
import { Redirect, RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import { Payment } from "sweet-global";
import ViewBooking from "sweet-web/components/Booking/ViewBooking";
import CaretIcon from "sweet-web/components/icons/CaretIcon";
import CrossIcon from "sweet-web/components/icons/CrossIcon";
import Button from "sweet-web/components/universal/Button";
import Input from "sweet-web/components/universal/Input";
import CardInput from "sweet-web/components/universal/Input/CardInput";
import SelectInput from "sweet-web/components/universal/Input/SelectInput";
import {
  ItemLabel,
  ItemTitle,
  Paragraph,
  SectionLabel,
  Title
} from "sweet-web/components/universal/Text";
import Toggle from "sweet-web/components/universal/Toggle";
import { PLAID_PUBLIC_KEY } from "sweet-web/keys";
import { fadeIn } from "sweet-web/lib/animations";
import { colors } from "sweet-web/lib/styles";
import { RootState } from "sweet-web/redux";
import { MakePayment } from "sweet-web/redux/booking/actions";
import * as selectors from "sweet-web/redux/selectors";

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding-top: 5%;
`;

const Exit = styled.button`
  outline: none;
  border: none;
  padding: 20px;
  position: absolute;
  top: 70px;
  left: 40px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  &:hover {
    p {
      color: ${colors.primary};
    }
  }
`;

const ExitText = styled.p`
  font-size: 14px;
  color: ${colors.gray};
  margin-left: 10px;
  font-weight: 500;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  max-width: 350px;
  margin-right: 40px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${colors.lighterGray};
`;

const Bank = styled.div`
  border-radius: 5px;
  border: 1px solid ${colors.nearBlack};
  padding: 5px 10px;
  color: ${colors.nearBlack};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  outline: none;
  background: ${colors.white};
  font-size: 14px;
  font-weight: 600;
  margin: 5px 0;
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
`;

const CancelBank = styled.div`
  padding-left: 15px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = RouteComponentProps & ConnectedProps<typeof connector>;

const Pay: React.FC<Props> = props => {
  const { bookingId } = props.match.params as { bookingId: string };
  const {
    booking,
    user,
    isMakingPayment,
    errorMakingPayment,
    userName,
    history
  } = props;

  const stripe = useStripe();

  const [paymentMethod, setPaymentMethod] = useState("CARD" as string);
  const [exitHover, setExitHover] = useState(false);
  const [bankData, setBankData] = useState(null as Payment["bank"] | null);

  if (booking.status === "INQUIRY") return <Redirect to="/" />;

  const submit = async () => {
    try {
      if (paymentMethod === "CARD") {
        const { source } = await stripe.createSource({
          type: "card",
          owner: {
            name: `${user.firstName} ${user.lastName}`
          }
        });

        await props.MakePayment(bookingId, source.id, null);
        history.push(`/c/${booking.chat}`);
      } else if (paymentMethod === "BANK") {
        if (!bankData) {
          alert("Please link your bank account");

          return;
        }
        await props.MakePayment(bookingId, null, bankData);
      }
    } catch (error) {
      alert(`Something went wrong: ${error}`);
    }
  };

  const goToChat = () => props.history.push(`/c/${booking.chat}`);

  const onBankExit = () => {};

  const onBankSuccess = (token, metadata) => {
    setBankData({
      name: metadata.institution.name,
      token,
      accountId: metadata.account_id
    });
  };

  return (
    <Wrapper>
      <Content>
        <Title style={{ marginTop: 10 }}>Confirm and Deposit</Title>
        <SectionLabel style={{ marginTop: 25 }}>
          How would you like to pay?
        </SectionLabel>
        <Toggle
          activeValue={paymentMethod}
          onChange={setPaymentMethod}
          values={[
            {
              name: "CARD",
              title: "Debit or credit card"
            },
            {
              name: "BANK",
              title: "Bank account ACH"
            },
            {
              name: "OTHER",
              title: "Someone else is paying (coming soon)",
              disabled: true
            }
          ]}
        />

        {paymentMethod === "CARD" && (
          <React.Fragment>
            <SectionLabel style={{ marginTop: 25 }}>Pay with</SectionLabel>
            <CardInput />
          </React.Fragment>
        )}
        {paymentMethod === "BANK" && !bankData && (
          <React.Fragment>
            <SectionLabel style={{ marginTop: 25 }}>
              Link your bank
            </SectionLabel>
            <PlaidLink
              clientName="Sweet"
              env="sandbox"
              product={["auth"]}
              publicKey={PLAID_PUBLIC_KEY}
              onExit={onBankExit}
              onSuccess={onBankSuccess}
              style={{
                fontSize: 14,
                color: colors.white,
                paddingLeft: 20,
                paddingRight: 20,
                paddingTop: 10,
                paddingBottom: 10,
                backgroundColor: colors.primary,
                borderRadius: 5,
                marginTop: 5,
                fontWeight: "700",
                outline: "none"
              }}
            >
              Get started
            </PlaidLink>
          </React.Fragment>
        )}
        {paymentMethod === "BANK" && bankData && (
          <React.Fragment>
            <ItemTitle style={{ marginTop: 25 }}>Linked bank</ItemTitle>
            <Bank>
              {bankData.name}
              <CancelBank onClick={() => setBankData(null)}>
                <CrossIcon
                  color={colors.gray}
                  style={{ marginTop: 5 }}
                  size={16}
                />
              </CancelBank>
            </Bank>
          </React.Fragment>
        )}

        <SectionLabel style={{ marginTop: 30, marginBottom: 10 }}>
          Cancellation policy: Moderate
        </SectionLabel>
        <Paragraph>
          Cancel at least one week in advance to receive a refund of your
          deposit. If you cancel within one week of your event, you will not
          receive a refund, and you will be charged for 50% of the booking cost.
        </Paragraph>

        <Divider />

        <Paragraph>
          I agree to the Event Rules, Payment Policy and Cancellation Policy,
          and to pay the total amount shown which includes a service fee.
        </Paragraph>
        {paymentMethod === "CARD" && (
          <Button
            title={"Confirm and deposit"}
            onClick={submit}
            style={{ marginTop: 25 }}
            error={errorMakingPayment}
            loading={isMakingPayment}
            small={true}
          />
        )}
        {paymentMethod === "BANK" && !bankData && (
          <Button
            title={"Pending bank connection"}
            style={{ marginTop: 25 }}
            small={true}
            disabled={true}
          />
        )}
        {paymentMethod === "BANK" && bankData && (
          <Button
            title={"Confirm and deposit"}
            onClick={submit}
            style={{ marginTop: 25 }}
            error={errorMakingPayment}
            loading={isMakingPayment}
            small={true}
          />
        )}
      </Content>
      <ViewBooking bookingId={bookingId} />
      <Exit
        onClick={goToChat}
        onMouseOver={() => setExitHover(true)}
        onMouseLeave={() => setExitHover(false)}
      >
        <CaretIcon
          color={exitHover ? colors.primary : colors.gray}
          style={{ transform: "rotate(180deg)", marginBottom: 5 }}
          size={16}
        />
        <ExitText>Back to chat</ExitText>
      </Exit>
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { bookingId } = ownProps.match.params as { bookingId: string };

  return {
    booking: selectors.getBooking(bookingId)(state),
    user: selectors.user(state),
    isMakingPayment: selectors.isMakingPayment(state),
    errorMakingPayment: selectors.errorMakingPayment(state),
    userName: selectors.userName(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      MakePayment
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default withRouter(connector(Pay));
